import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Label, Modal } from "reactstrap";
import { InputField } from "../../Component/Common/InputField";
import SwitchField from "../../Component/Common/SwitchField";
import { CustomDatePicker } from "../../Component/Common/ConstomModal/CustomDatePicker";
import { ACCESS, FEATURES, checkFormError, formatDate, hasAccess } from "../../helper";
import { addSortVoterValidation } from "../../helper/errorSchema";
import { getStorage } from "../../helper/storage";
import { addSortVoter, singleSortVoter, updateSortVoter } from "../../services/voterService";
import {
  APP_INFO_FIELDS,
  FORM_SECTIONS,
  MEDIA_FIELDS,
  READONLY_MEDIA_FIELDS,
  TEXT_FIELDS,
  UPLOAD_FIELD_NAMES,
  formatInfoValue,
  hasValue,
  isImageUrl,
} from "./appFields";

// Editing keeps already-uploaded files, so uploads aren't required then.
const editAppValidation = addSortVoterValidation.omit(UPLOAD_FIELD_NAMES);

const emptyOrderObj = () => ({
  banner_send_date: formatDate(new Date(), "-"),
  assembly_number: "",
  is_enable: false,
  is_banner_enable: false,
  ...Object.fromEntries(TEXT_FIELDS.map((field) => [field.name, ""])),
  ...Object.fromEntries(UPLOAD_FIELD_NAMES.map((name) => [name, ""])),
});

// Form values from a GET sortVoter/<uuid> response. The record's fields come
// back flattened on the response itself (res.app_name, res.extra20, ...) -
// there's no res.results wrapper, which is why reading res.results.* left the
// whole Edit form empty. null becomes "" so untouched fields don't count as
// "changed" when only changed fields are sent.
const recordToForm = (record) => ({
  ...emptyOrderObj(),
  banner_send_date: record.banner_send_date || "",
  assembly_number: Array.isArray(record.assembly_number)
    ? record.assembly_number
    : record.assembly_number
    ? String(record.assembly_number).split(",")
    : "",
  is_enable: record.is_enable === true,
  is_banner_enable: record.is_banner_enable === true,
  ...Object.fromEntries(TEXT_FIELDS.map((field) => [field.name, record[field.name] ?? ""])),
});

const FilePreview = ({ url, label }) =>
  isImageUrl(url) ? (
    <a href={url} target="_blank" rel="noreferrer" className="app-file-preview" title={`Open ${label}`}>
      <img src={url} alt={label} />
    </a>
  ) : (
    <a href={url} target="_blank" rel="noreferrer" className="app-file-link">
      Current file ↗
    </a>
  );

// The "Add App" offcanvas from the old SortVoter.js, moved to its own routed
// page - also handles Edit (AppList's edit action ->
// /application-management/add?editId=<uuid>) and read-only View (AppList's
// eye icon -> ...&view=1), same pattern as AddVoter.js/AddUser.js.
export const AddApp = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");
  const isView = !!editId && searchParams.get("view") === "1";
  const userLogin = getStorage("logged-in");
  const canEdit = hasAccess(userLogin?.user_type, FEATURES.SYSTEM_SETTINGS, ACCESS.LIMITED);

  const [isEdit, setIsEdit] = useState(!!editId);
  const [record, setRecord] = useState(null);
  const [formOrderValue, setFormOrderValue] = useState(emptyOrderObj());
  const [initialForm, setInitialForm] = useState(emptyOrderObj());
  const [formError, setFormError] = useState({});
  const [updateId, setUpdateId] = useState("");
  const [isLoadding, setIsLoadding] = useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState(false);

  const avfBodyRef = useRef(null);
  const avfSectionRefs = useRef({});
  const avfScrollLockRef = useRef(false);
  const avfScrollLockTimeoutRef = useRef(null);

  // Optional fields are visible when the loaded record has data for them,
  // when the user has typed into them, or when "Show empty fields" is on.
  const isFieldVisible = (name) =>
    isView
      ? hasValue(record?.[name])
      : showEmptyFields || hasValue(record?.[name]) || hasValue(formOrderValue[name]);

  const visibleSections = useMemo(
    () =>
      FORM_SECTIONS.map((section) => ({
        ...section,
        optionalVisible: (section.optional || []).filter((field) => isFieldVisible(field.name)),
      })).filter(
        (section) =>
          ["settings", "media"].includes(section.id) ||
          (section.core || []).length > 0 ||
          section.optionalVisible.length > 0
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record, formOrderValue, showEmptyFields]
  );

  const [activeSection, setActiveSection] = useState(FORM_SECTIONS[0].id);

  useEffect(() => () => window.clearTimeout(avfScrollLockTimeoutRef.current), []);

  const registerSectionRef = (id) => (el) => {
    avfSectionRefs.current[id] = el;
  };

  const scrollToSection = (id) => {
    const container = avfBodyRef.current;
    const target = avfSectionRefs.current[id];
    if (!container || !target) return;
    avfScrollLockRef.current = true;
    window.clearTimeout(avfScrollLockTimeoutRef.current);
    avfScrollLockTimeoutRef.current = window.setTimeout(() => {
      avfScrollLockRef.current = false;
    }, 600);
    container.scrollTo({ top: target.offsetTop - 8, behavior: "smooth" });
    setActiveSection(id);
  };

  const handleAvfBodyScroll = () => {
    if (avfScrollLockRef.current) return;
    const container = avfBodyRef.current;
    if (!container) return;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
    if (atBottom) {
      setActiveSection(visibleSections[visibleSections.length - 1].id);
      return;
    }
    const scrollPos = container.scrollTop + 48;
    let current = visibleSections[0].id;
    visibleSections.forEach((section) => {
      const target = avfSectionRefs.current[section.id];
      if (target && target.offsetTop <= scrollPos) current = section.id;
    });
    setActiveSection(current);
  };

  const goBackToList = () => navigate("/application-management/list");

  const validationSchema = isEdit ? editAppValidation : addSortVoterValidation;

  const handleOrderChange = async (name, value) => {
    const stateObj = { ...formOrderValue, [name]: value };
    setFormOrderValue(stateObj);
    if (Object.keys(formError || {}).length) {
      const error = await checkFormError(stateObj, validationSchema);
      setFormError(error === true ? {} : { ...error });
    }
  };

  const onSuccessResponse = () => {
    queryClient.refetchQueries("getSortVoter");
    setIsLoadding(false);
    goBackToList();
  };

  const addMutation = useMutation(addSortVoter, {
    onSuccess: (res) => {
      if (res?.success) onSuccessResponse();
      else setIsLoadding(false);
    },
  });

  const updateMutation = useMutation(updateSortVoter, {
    onSuccess: (res) => {
      if (res?.success) onSuccessResponse();
      else setIsLoadding(false);
    },
  });

  const getOrderDetails = useMutation(singleSortVoter, {
    onSuccess: (res) => {
      if (!res?.success) return;
      // Older API versions wrapped the record in `results`; current one doesn't.
      const data = res?.results && typeof res.results === "object" && !Array.isArray(res.results) ? res.results : res;
      const details = recordToForm(data);
      setRecord(data);
      setFormOrderValue(details);
      setInitialForm(details);
      setIsEdit(true);
      setFormError({});
      setUpdateId(data?.uuid || data?.id || editId);
    },
  });

  useEffect(() => {
    if (editId) getOrderDetails.mutate(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const onOrderSubmit = async () => {
    const validationResult = await checkFormError({ ...formOrderValue }, validationSchema);

    const formData = new FormData();
    Object.keys(formOrderValue).forEach((item) => {
      if (item === "banner_send_date") return;
      if (formOrderValue[item] !== initialForm[item]) formData.append(item, formOrderValue[item]);
    });
    formData.append("banner_send_date", formOrderValue.banner_send_date);
    if (userLogin?.app_name) formData.append("app_name", userLogin.app_name);
    if (userLogin?.party_name) formData.append("party_name", userLogin.party_name);

    if (validationResult === true) {
      setIsLoadding(true);
      if (isEdit) {
        updateMutation.mutate({ formdata: formData, id: updateId });
      } else {
        addMutation.mutate(formData);
      }
    } else {
      setIsLoadding(false);
      setFormError(validationResult);
    }
  };

  const renderTextField = (field, { required = false } = {}) => (
    <div className={`avf-field ${field.multiline ? "avf-field-wide" : ""}`} key={field.name}>
      <InputField
        floating={!field.multiline}
        label={field.label}
        labelClass="FormLableKaran"
        className="modal_input orderModal_Input"
        placeholder={`Enter ${field.label}`}
        name={field.name}
        type={field.multiline ? "textarea" : field.type || "text"}
        rows={field.multiline ? 4 : undefined}
        disabled={isView}
        labelSpan={required && !isView ? "*" : undefined}
        focus={!!formError?.[field.name]}
        error={formError[field.name]}
        value={formOrderValue?.[field.name] ?? ""}
        onChange={({ target: { name, value } }) =>
          handleOrderChange(name, field.type === "url" ? value.trim() : value)
        }
      />
    </div>
  );

  const appInfo = isEdit ? APP_INFO_FIELDS.filter((field) => hasValue(record?.[field.name])) : [];
  const readonlyMedia = READONLY_MEDIA_FIELDS.filter((field) => hasValue(record?.[field.name]));
  const hiddenEmptyCount = FORM_SECTIONS.flatMap((section) => section.optional || []).filter(
    (field) => !isFieldVisible(field.name)
  ).length;

  return (
    <Modal isOpen toggle={goBackToList} centered className="avf-dialog" contentClassName="avf-content">
      <div className="avf-header">
        <div className="avf-header-top">
          <div>
            <h2 className="avf-title">{isView ? "View App" : isEdit ? "Edit App" : "Add App"}</h2>
            <p className="avf-subtitle">
              {isEdit && record?.app_name
                ? `${record.app_name}${record.party_name ? ` · ${record.party_name}` : ""}`
                : "Fill in the app's details across the sections below"}
            </p>
          </div>
          <button type="button" className="avf-close-btn" onClick={goBackToList} aria-label="Close">
            ×
          </button>
        </div>
        <div className="avf-rail">
          {visibleSections.map((section, index) => (
            <button
              key={section.id}
              type="button"
              className={`avf-step ${activeSection === section.id ? "is-active" : ""}`}
              onClick={() => scrollToSection(section.id)}
            >
              <span className="avf-step-num">{index + 1}</span>
              <span className="avf-step-label">{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="avf-body" ref={avfBodyRef} onScroll={handleAvfBodyScroll}>
        {appInfo.length > 0 && (
          <div className="app-info-grid">
            {appInfo.map((field) => (
              <div className="app-info-item" key={field.name}>
                <span className="app-info-label">{field.label}</span>
                <span className="app-info-value">{formatInfoValue(record[field.name], field)}</span>
              </div>
            ))}
          </div>
        )}

        {visibleSections.map((section, index) => (
          <div className="avf-section" ref={registerSectionRef(section.id)} key={section.id}>
            <div className="avf-section-head">
              <span className="avf-section-num">{index + 1}</span>
              <h3 className="avf-section-title">{section.label}</h3>
              {index === 0 && !isView && (hiddenEmptyCount > 0 || showEmptyFields) && (
                <div className="app-show-empty">
                  <SwitchField
                    label={showEmptyFields ? "Hide empty fields" : `Show empty fields (${hiddenEmptyCount})`}
                    value={showEmptyFields}
                    onSwitchChange={() => setShowEmptyFields((prev) => !prev)}
                  />
                </div>
              )}
            </div>

            {section.id === "settings" && (
              <>
                <div className="avf-grid">
                  <div className="avf-field">
                    <Label className="FormLableKaran">Banner Send Date</Label>
                    <CustomDatePicker
                      value={formOrderValue?.banner_send_date || ""}
                      type="date"
                      focus={!!formError?.banner_send_date}
                      error={formError.banner_send_date}
                      name="banner_send_date"
                      disabled={isView}
                      onSelect={({ target: { name, value } }) => handleOrderChange(name, value)}
                      className="date-input"
                    />
                  </div>
                  <div className="avf-field">
                    <InputField
                      floating
                      className="modal_input orderModal_Input"
                      label="Assembly Number"
                      labelClass="FormLableKaran"
                      placeholder="Enter Assembly Number"
                      name="assembly_number"
                      type="text"
                      labelSpan={isView ? undefined : "*"}
                      disabled={isView}
                      focus={!!formError?.assembly_number}
                      error={formError.assembly_number}
                      value={
                        Array.isArray(formOrderValue?.assembly_number)
                          ? formOrderValue.assembly_number.join(",")
                          : formOrderValue?.assembly_number || ""
                      }
                      onChange={({ target: { name, value } }) => handleOrderChange(name, value.split(","))}
                    />
                  </div>
                  {section.optionalVisible.map((field) => renderTextField(field))}
                </div>
                <div className="avf-checkbox-row">
                  <div className="anvOt_inputField checkboxtext">
                    <SwitchField
                      label="Enable"
                      disabled={isView}
                      value={formOrderValue.is_enable === true}
                      onSwitchChange={({ target: { checked } }) => handleOrderChange("is_enable", checked)}
                    />
                  </div>
                  <div className="anvOt_inputField checkboxtext">
                    <SwitchField
                      label="Banner Enable"
                      disabled={isView}
                      value={formOrderValue.is_banner_enable === true}
                      onSwitchChange={({ target: { checked } }) => handleOrderChange("is_banner_enable", checked)}
                    />
                  </div>
                </div>
              </>
            )}

            {section.id !== "settings" && section.id !== "media" && (
              <div className="avf-grid">
                {(section.core || []).map((field) => renderTextField(field, { required: true }))}
                {section.optionalVisible.map((field) => renderTextField(field))}
              </div>
            )}

            {section.id === "media" && (
              <div className="avf-grid">
                {MEDIA_FIELDS.map((field) => {
                  const currentUrl = record?.[field.current];
                  const required = !isEdit && !field.optionalUpload;
                  // View: just the files that exist, no upload inputs.
                  if (isView) {
                    return hasValue(currentUrl) ? (
                      <div className="avf-field" key={field.name}>
                        <Label className="FormLableKaran">{field.label}</Label>
                        <FilePreview url={currentUrl} label={field.label} />
                      </div>
                    ) : null;
                  }
                  return (
                    <div className="avf-field" key={field.name}>
                      <InputField
                        className="modal_input orderModal_Input"
                        label={field.label}
                        labelClass="FormLableKaran"
                        placeholder={`Upload ${field.label}`}
                        name={field.name}
                        type="file"
                        labelSpan={required ? "*" : undefined}
                        focus={!!formError?.[field.name]}
                        error={formError[field.name]}
                        onChange={({ target: { name, files } }) => handleOrderChange(name, files[0] || "")}
                      />
                      {hasValue(currentUrl) && <FilePreview url={currentUrl} label={field.label} />}
                    </div>
                  );
                })}
                {readonlyMedia.map((field) => (
                  <div className="avf-field" key={field.name}>
                    <Label className="FormLableKaran">{field.label}</Label>
                    <FilePreview url={record[field.name]} label={field.label} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="avf-footer">
        <div className="avf-footer-note">
          {isView ? "Read-only view" : isEdit ? "Only changed fields are saved" : "Fill in the required fields"}
        </div>
        {isView ? (
          <div className="avf-footer-actions">
            <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToList}>
              Close
            </button>
            {canEdit && (
              <button
                type="button"
                className="avf-btn avf-btn-primary"
                onClick={() => navigate(`/application-management/add?editId=${editId}`)}
              >
                Edit App
              </button>
            )}
          </div>
        ) : (
          <div className="avf-footer-actions">
          <button type="button" className="avf-btn avf-btn-secondary" onClick={goBackToList}>
            Cancel
          </button>
          {isLoadding ? (
            <button type="button" className="avf-btn avf-btn-primary" disabled>
              Processing...
            </button>
          ) : (
            <button type="button" className="avf-btn avf-btn-primary" onClick={onOrderSubmit}>
              {isEdit ? "Update App" : "Create App"}
            </button>
          )}
          </div>
        )}
      </div>
    </Modal>
  );
};
